import { useState, useEffect, useCallback } from 'react';
import { Product } from '../types/product';
import { getProductDetail } from '../api/productService';

interface UseProductResult {
  product: Product | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * 개별 상품 정보를 관리하는 커스텀 훅
 * @param productId 상품 ID
 * @returns 상품 정보, 로딩 상태, 에러 상태, 재조회 함수
 */
export const useProduct = (productId: string): UseProductResult => {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProduct = useCallback(async () => {
    if (!productId) {
      setError('상품 ID가 필요합니다.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const productData = await getProductDetail(productId);
      setProduct(productData);
    } catch (err) {
      console.error('[useProduct] 상품 정보 조회 실패:', err);
      setError(err instanceof Error ? err.message : '상품 정보를 불러오는데 실패했습니다.');
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  // 상품 ID가 변경되면 데이터 재조회
  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // 재조회 함수
  const refetch = useCallback(() => {
    fetchProduct();
  }, [fetchProduct]);

  return {
    product,
    loading,
    error,
    refetch
  };
}; 